import { gql } from '@apollo/client';
// Fragments
import { PublicTicketEventFields } from 'api/ticketEvents/fragments';
import { StoreDetailsFields } from 'api/store/fragments';
import {
  StreamFields,
  StreamNoteFields,
  GetUpcomingStreamsEntities,
  SoonestStreamFields,
} from './fragments';
import { ThankYouVideoFields } from 'api/thankYouVideo/fragments';

export const MY_STREAMS = gql`
  query MyStreams($input: MyStreamsFilterInput) {
    myStreams(input: $input) {
      total
      offset
      limit
      entities {
        ...StreamFields
        isHidden
        affiliateEnabled
      }
    }
  }
  ${StreamFields}
`;

export const MY_SOONEST_STREAMS = gql`
  query MySoonestStream($input: MyStreamsFilterInput) {
    myStreams(input: $input) {
      total
      offset
      limit
      entities {
        ...SoonestStreamFields
        isHidden
      }
    }
  }
  ${SoonestStreamFields}
`;

export const MY_OUTCOMING_STREAM_ORDERS = gql`
  query MyOutcomingStreamOrders($input: OutcomingStreamInput) {
    myOutcomingStreamOrders(input: $input) {
      total
      offset
      limit
      entities {
        id
        orderStatus
        paymentMethodId
        paidPrice
        paidDiscountAmount
        stream {
          isFree
          id
          slug
          name
          description
          isInterview
          imageURL
          streamStatus
          scheduleDate
          scheduleDateUtc
          repeatsEveryEnum
          streamTips {
            id
            amount
            message
          }
          timeZone {
            tzCode
            offset
          }
          store {
            id
            slug
            lastName
            firstName
            storeDetails {
              ...StoreDetailsFields
            }
          }
          isPurchased
        }
      }
    }
  }
  ${StoreDetailsFields}
`;

export const GET_STREAM = gql`
  query GetStream($input: GetStreamInput!) {
    stream(input: $input) {
      id
      slug
      name
      description
      imageURL
      price
      isFree
      isHidden
      streamStatus
      chatEnabled
      repeatsEveryEnum
      interview {
        id
        status
        chatEnabled
        interviewer {
          id
          slug
          firstName
          lastName
          audience
          storeDetails {
            storeName
            avatarURL
          }
        }
        interviewee {
          id
          slug
          firstName
          lastName
          audience
          storeDetails {
            storeName
            avatarURL
          }
        }
        source {
          type
          ... on InterviewSourcePPVParticipant {
            stream {
              id
            }
          }
        }
      }
      streamStatus
      scheduleDate
      scheduleDateUtc
      type
      isInterview
      hashtags {
        name
        id
      }
      mentions {
        id
        mentionType
        store {
          slug
          id
          role
          storeDetails {
            id
            storeName
            storeId
            avatarURL
          }
          firstName
          lastName
        }
        targetType
        unregisteredContact {
          id
          name
          representativeName
          phoneNumber
          type
          email
          name
          representativeName
          socialMediaLink
          url
        }
      }
      moderator {
        id
        firstName
        lastName
      }
      streamNotes {
        ...StreamNoteFields
      }
      sponsors {
        id
        logoUrl
        name
        promoMessage
      }
      timeZone {
        tzCode
        offset
      }
      isPurchased
      streamRecords {
        id
        aspectRatio
        streamId
        videoFileKey
        videoURL
        duration
        videoThumbnailURL
      }
      streamRecordsV2 {
        groupId
        ar {
          landscape {
            Mp4 {
              id
              streamId
              videoURL
              videoThumbnailURL
              duration
              aspectRatio
            }
          }
          portrait {
            Mp4 {
              id
              streamId
              videoURL
              videoThumbnailURL
              duration
              aspectRatio
            }
          }
        }
      }
      store {
        id
        slug
        lastName
        firstName
        email
        role
        audience
        socialMedia {
          url
          type
        }
        amas {
          id
          type
          price
          daysToResponse
          videoURL
          videoThumbnailURL
          status
        }
        status
        hashtags {
          id
          name
        }
        hasMerch
        hasAma
        hasMediaPost
        hasProducts
        hasMemorabilia
        hasExperiences
        storeDetails {
          id
          storeName
          avatarURL
          description
          bio
        }
      }
      participants {
        id
        firstName
        lastName
        slug
        avatarUrl
        storeDetails {
          id
          storeName
          avatarURL
        }
      }
      numberOfOrders
      streamRevenue
      affiliateEnabled
      affiliateCommissionPercent
      eventAnalytics {
        streamId
        signUps
        interviews {
          id
          status
          stream {
            id
            slug
            store {
              id
              slug
            }
          }
          interviewee {
            id
            email
            firstName
            lastName
            avatarUrl
            slug
            storeDetails {
              id
              storeName
            }
          }
        }
      }
      views {
        totalViewCount
      }
      fanPollingEnabled
      sharePickGraphicsEnabled
      postEventContentHubEnabled
      ticketEvent {
        ...PublicTicketEventFields
      }
    }
  }
  ${StreamNoteFields}
  ${PublicTicketEventFields}
`;

export const GET_STREAM_HIGHLIGHTS = gql`
  query GetStreamHighlights($input: GetStreamInput!) {
    stream(input: $input) {
      id
      ppvHighlights {
        id
        title
        description
        clipUrl
        thumbnailUrl
        score
        createdAt
      }
    }
  }
`;

export const GET_STREAM_BY_INVITE = gql`
  query GetStreamByInvite($input: GetStreamByInviteInput!) {
    getStreamByInvite(input: $input) {
      isInterview
      streamStatus
      isHidden
      store {
        slug
      }
      slug
    }
  }
`;

export const GET_STREAM_EXTENDED = gql`
  query GetStreamExtended($input: GetStreamInput!) {
    stream(input: $input) {
      id
      slug
      name
      description
      imageURL
      imageFileKey
      price
      hashtags {
        name
        id
      }
      mentions {
        id
        mentionType
        store {
          id
          slug
          role
          storeDetails {
            id
            storeName
            storeId
            avatarURL
          }
          firstName
          lastName
        }
        targetType
        unregisteredContact {
          id
          name
          representativeName
          phoneNumber
          type
          email
          name
          representativeName
          socialMediaLink
          url
        }
      }
      requestedPrice
      isFree
      affiliateEnabled
      affiliateCommissionPercent
      repeatsEveryEnum
      repeatingTitle
      isHidden
      streamStatus
      chatEnabled
      scheduleDate
      isInterview
      scheduleDateTz
      scheduleDateUtc
      moderator {
        id
        firstName
        lastName
      }
      streamNotes {
        ...StreamNoteFields
      }
      sponsors {
        id
        logoUrl
        name
        promoMessage
      }
      timeZone {
        tzCode
        offset
      }
      isPurchased
      streamRecords {
        aspectRatio
        id
        streamId
        videoFileKey
        videoURL
        duration
        videoThumbnailURL
      }
      streamRecordsV2 {
        groupId
        ar {
          landscape {
            Mp4 {
              id
              streamId
              videoURL
              videoThumbnailURL
              duration
              aspectRatio
            }
          }
          portrait {
            Mp4 {
              id
              streamId
              videoURL
              videoThumbnailURL
              duration
              aspectRatio
            }
          }
        }
      }
      store {
        id
        slug
        lastName
        firstName
        email
        role
        amas {
          id
          type
          price
          daysToResponse
          videoURL
          videoThumbnailURL
          status
        }
        status
        hashtags {
          id
          name
        }
        hasMerch
        hasAma
        hasMediaPost
        hasProducts
        hasMemorabilia
        hasPureProducts
        hasExperiences
        hasStreams
        hasActiveStreams
        hasArticle
        hasTicketEvents
        storeDetails {
          id
          storeName
          avatarURL
          description
          bio
        }
      }
    }
  }
  ${StreamNoteFields}
`;

export const GET_STREAMS = gql`
  query GetStreams(
    $input: StreamFilterInput!
    $withFilters: Boolean = false
    $withSponsors: Boolean = false
  ) {
    streamsV2(input: $input) {
      entities {
        ...GetUpcomingStreamsEntities
        sponsors @include(if: $withSponsors) {
          id
          logoUrl
          name
          promoMessage
        }
      }
      total
      offset
      limit
      filtersV2 @include(if: $withFilters) {
        scheduleDate
        price
        streamStatusV2
        streamType
      }
    }
  }
  ${GetUpcomingStreamsEntities}
`;

export const SEARCH_STREAMS = gql`
  query SearchStreams($input: StreamFilterInput!) {
    streamsV2(input: $input) {
      entities {
        id
        name
        store {
          id
          slug
          lastName
          firstName
          storeDetails {
            id
            storeName
          }
        }
      }
    }
  }
`;

export const GET_ATHLETE_STREAMS = gql`
  query GetAthleteStreams(
    $liveAndUpcomingStreamsInput: StreamFilterInput!
    $pastStreamsInput: StreamFilterInput!
    $withFilters: Boolean = false
  ) {
    liveAndUpcomingStreams: streamsV2(input: $liveAndUpcomingStreamsInput) {
      entities {
        ...GetUpcomingStreamsEntities
      }
      total
      offset
      limit
      filtersV2 @include(if: $withFilters) {
        scheduleDate
        price
        streamStatusV2
      }
    }
    pastStreams: streamsV2(input: $pastStreamsInput) {
      entities {
        ...GetUpcomingStreamsEntities
      }
      total
      offset
      limit
      filtersV2 @include(if: $withFilters) {
        scheduleDate
        price
        streamStatusV2
      }
    }
  }
  ${GetUpcomingStreamsEntities}
`;

export const GET_MY_STREAM_SALES = gql`
  query GetStreamSales($input: StreamSalesInput) {
    getStreamSales(input: $input) {
      total
      offset
      limit
      entities {
        stream {
          id
          name
          streamStatus
          scheduleDate
          repeatsEveryEnum
        }
        thankYouVideo {
          ...ThankYouVideoFields
        }
        requestedProfit
        orderAmount
        tipAmount
      }
    }
  }
  ${ThankYouVideoFields}
`;

export const GET_MY_STREAM_ORDER_SALES = gql`
  query GetStreamOrderSales(
    $input: StreamOrderAndTipsTypeInput
    $streamId: String!
  ) {
    getStreamOrderSales(input: $input, streamId: $streamId) {
      total
      offset
      limit
      entities {
        id
        firstName
        lastName
        paidat
        earnings
        discount
        type
      }
    }
  }
`;

export const GET_REGISTERED_STREAM_USERS = gql`
  query GetRegisteredStreamUsers($input: GetStreamUsersInput!) {
    getRegisteredStreamUsers(input: $input) {
      total
      offset
      limit
      entities {
        id
        firstName
        lastName
      }
    }
  }
`;

export const GET_AFFILIATE_STREAM_PRODUCTS = gql`
  query GetAffiliateStreamProducts($input: GetAffiliateStreamProductsInput!) {
    getAffiliateStreamProducts(input: $input) {
      id
      imageKey
      imageUrl
      orderIndex
      price
      title
      url
    }
  }
`;

export const GET_STORE_STREAMS = gql`
  query GetStoreStreams($input: GetStoreStreamsInput!) {
    getStoreStreams(input: $input) {
      entities {
        ...GetUpcomingStreamsEntities
        isHidden
      }
      total
      offset
      limit
    }
  }
  ${GetUpcomingStreamsEntities}
`;

export const GET_STREAM_PARTICIPANTS = gql`
  query GetStreamParticipants($input: StreamParticipantFilterInput!) {
    getStreamParticipants(input: $input) {
      entities {
        id
        firstName
        lastName
        avatarUrl
        slug
        role
        storeDetails {
          id
          storeName
          avatarURL
        }
      }
      total
      offset
      limit
    }
  }
`;

export const GET_OR_CREATE_STREAM_PARTICIPANT_INVITE = gql`
  query GetOrCreateStreamParticipantInvite(
    $input: StreamParticipantInviteInput!
  ) {
    getOrCreateStreamParticipantInvite(input: $input) {
      id
      invite
    }
  }
`;

export const GET_STREAM_REDUCED = gql`
  query GetStreamReduced($input: GetStreamInput!) {
    stream(input: $input) {
      id
      slug
      isFree
      isHidden
      isPurchased
      streamStatus
      scheduleDate
      timeZone {
        offset
      }
      store {
        id
        slug
      }
      interview {
        id
        streamCohostToken
      }
    }
  }
`;

export const ESTIMATE_STREAM_PRICE = gql`
  query EstimateStreamPrice($input: EstimateStreamPriceInput!) {
    estimateStreamPrice(input: $input) {
      profit
    }
  }
`;

export const PPV_TIERS = gql`
  query PpvTiers {
    ppvTiers {
      id
      sku
      price
      iosPrice
      androidPrice
    }
  }
`;

export const GET_RECENT_STREAMS = gql`
  query GetRecentStreams($input: StreamFilterInput!) {
    streamsV2(input: $input) {
      entities {
        id
        name
        scheduleDateUtc
      }
    }
  }
`;
